import { useEffect, useState } from "react";
import { atom, useAtom } from "jotai";

import useUpdateGPTConversation from "../../api/api-hooks/conversations/useUpdateGPTConversations.ts/useUpdateGPTConversation";
import useConversation from "../../api/api-hooks/conversations/useConversation";
import { BriePages, SetBriePage, SetConvoID } from "./BrieChatMenu";
import CenteredSpinner from "../../elements/CenteredSpinner";
import useUser from "../../api/api-hooks/auth/useUser";
import { SearchGPTMessage } from "./GPTConvoTypes";
import BrieVoiceInput from "./BrieVoiceInput";
import StrUtils from "../../utils/StrUtils";
import BrieChat from "./BrieChat";

interface SearchGPTProps {
  page: BriePages;
  setPage: SetBriePage;
  conversationID: string;
  setConversationID: SetConvoID;
  pageID: string | undefined;
  viewID: string | undefined;
}

const defaultMessages: [] = [];

// This atom allows other components to provide a command to Brie Chat.
export const brieCommandTextAtom = atom("");

const BrieChatSearchGPT = ({
  page,
  setPage,
  conversationID,
  setConversationID,
  pageID,
  viewID,
}: SearchGPTProps) => {
  const [inputText, setInputText] = useState("");
  const [providedCommand, setProvidedCommand] = useAtom(brieCommandTextAtom);
  const { conversation, conversationLoading } = useConversation(conversationID);
  const { updateGPTConvo, gptProcessing } = useUpdateGPTConversation({
    pageID,
    viewID,
  });
  const { user } = useUser();
  const [tempMessages, setTempMessages] = useState<SearchGPTMessage[]>([]);
  const isNew = conversationID === "new";

  const messages = isNew ? tempMessages : conversation?.Messages;
  const isVoice = page.startsWith("voice");

  // Allows other components to provide a command to Brie Chat.
  useEffect(() => {
    if (!providedCommand || !conversationID) return;

    sendMessage(providedCommand);
    setProvidedCommand("");
  }, [conversationID]);

  return (
    <>
      {conversationLoading && <CenteredSpinner size="xl" />}
      {!conversationLoading && !isVoice && (
        <BrieChat
          messages={messages || defaultMessages}
          onSend={sendMessage}
          isTyping={gptProcessing}
          disableInput={gptProcessing}
          inputText={inputText}
          setInputText={setInputText}
          onUseVoice={() => setPage("voice-chat-gpt")}
          conversationID={conversationID}
        />
      )}
      {!conversationLoading && isVoice && (
        <BrieVoiceInput onSuccess={handleTranscriptSuccess} />
      )}
    </>
  );

  async function sendMessage(inputText: string) {
    const messagePayload: SearchGPTMessage = {
      id: StrUtils.uuidv4(),
      Type: "User",
      Message: inputText,
      Sender: user?.id,
      Reciever: "Brie",
    };

    if (isNew) {
      setTempMessages((prev) => [...prev, messagePayload]);
    }

    updateGPTConvo({
      conversationID: conversationID,
      message: messagePayload,
      onSuccess: (conversationID: string) => {
        setConversationID(conversationID);
      },
    });
  }

  function handleTranscriptSuccess(transcript: string) {
    setInputText(transcript);
    setPage("chat-gpt");
  }
};

export default BrieChatSearchGPT;
